<script setup>
/**
 * Marketing Consent Modal
 * Asks client users if they want to receive marketing content (promotions, discounts)
 * via email/WhatsApp/notifications. Shown until the client has a recorded answer
 * (`marketing.answered === true`). Yes AND No are both recorded, so a real "No" is never
 * re-asked. Closing with X records nothing → asked again next visit.
 *
 * Writes through recordMarketingConsent() (src/core/services/clients.js) — the one shared
 * writer, which also dual-writes the legacy flat `tos*` fields during the migration.
 */
import { ref } from 'vue'
import { useAuthStore } from '@app/store/auth.js'
import { recordMarketingConsent } from '@core/services/clients.js'
import { CONSENT_SOURCE } from '@core/config/consentConstants.js'

defineProps({
  open: { type: Boolean, default: false },
})

const emit = defineEmits(['close'])

const authStore = useAuthStore()
const saving = ref(false)

async function recordAnswer(consent) {
  if (saving.value) return
  saving.value = true
  try {
    const clientMakat = authStore.user.client?.makat
    const uid = authStore.userId
    if (clientMakat) {
      const marketing = await recordMarketingConsent({
        clientId: clientMakat,
        consent,
        uid,
        source: CONSENT_SOURCE.APP,
        previousMarketing: authStore.user.client?.marketing || null,
      })
      // Update local client object so the modal doesn't re-trigger
      authStore.user.client.marketing = { ...marketing, answeredAt: new Date() }
      authStore.user.client.tos = consent
      authStore.user.client.tos_answered = true
    }
  } catch (err) {
    console.error('[MarketingConsentModal] Failed to record marketing consent:', err)
  } finally {
    saving.value = false
    emit('close')
  }
}

function accept() {
  recordAnswer(true)
}

function decline() {
  recordAnswer(false)
}

function dismiss() {
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="consent-modal">
      <div v-if="open" class="consent-overlay" @click.self="dismiss">
        <div class="consent-modal">
          <!-- Close button -->
          <button class="consent-close" @click="dismiss" :disabled="saving">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <!-- SVG Illustration -->
          <div class="consent-illustration">
            <svg
              viewBox="0 0 120 120"
              width="120"
              height="120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <!-- Outer circle glow -->
              <circle cx="60" cy="60" r="56" fill="var(--bg-secondary)" />
              <circle cx="60" cy="60" r="48" fill="var(--bg-tertiary)" />

              <!-- Envelope body -->
              <rect
                x="26"
                y="42"
                width="68"
                height="46"
                rx="4"
                fill="var(--color-primary)"
                opacity="0.12"
                stroke="var(--color-primary)"
                stroke-width="2"
              />

              <!-- Envelope flap -->
              <path
                d="M26 46l34 22 34-22"
                stroke="var(--color-primary)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />

              <!-- Letter coming out -->
              <rect
                x="38"
                y="30"
                width="44"
                height="34"
                rx="3"
                fill="white"
                stroke="var(--color-primary)"
                stroke-width="1.5"
              />

              <!-- Letter lines -->
              <line
                x1="46"
                y1="40"
                x2="74"
                y2="40"
                stroke="var(--color-primary-light)"
                stroke-width="2"
                stroke-linecap="round"
                opacity="0.5"
              />
              <line
                x1="46"
                y1="47"
                x2="68"
                y2="47"
                stroke="var(--color-primary-light)"
                stroke-width="2"
                stroke-linecap="round"
                opacity="0.35"
              />
              <line
                x1="46"
                y1="54"
                x2="62"
                y2="54"
                stroke="var(--color-primary-light)"
                stroke-width="2"
                stroke-linecap="round"
                opacity="0.2"
              />

              <!-- Star / sparkle top-right -->
              <path
                d="M88 28l2 5 5 2-5 2-2 5-2-5-5-2 5-2z"
                fill="var(--color-warning)"
                opacity="0.8"
              />

              <!-- Small dot top-left -->
              <circle cx="30" cy="32" r="3" fill="var(--color-info)" opacity="0.4" />

              <!-- Notification badge -->
              <circle cx="82" cy="38" r="8" fill="var(--color-success)" />
              <path
                d="M79 38l2 2 4-4"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <!-- Content -->
          <div class="consent-content">
            <h2 class="consent-title">מבצעים, הנחות ותוכן שיווקי</h2>
            <p class="consent-description">
              האם תרצו לקבל עדכונים על מבצעים, הנחות בלעדיות ותוכן שיווקי במייל ובווטסאפ?
            </p>
          </div>

          <!-- Actions -->
          <div class="consent-actions">
            <button class="consent-accept-btn" @click="accept" :disabled="saving">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              כן, אני מעוניין/ת
            </button>
            <button class="consent-decline-link" @click="decline" :disabled="saving">
              תודה, לא מעוניין/ת
            </button>
          </div>

          <!-- Footer note -->
          <p class="consent-footnote">ניתן לשנות את הבחירה בכל שלב דרך עמוד הפרופיל שלך.</p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.consent-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: var(--spacing-md);
}

.consent-modal {
  position: relative;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl) var(--spacing-lg) var(--spacing-lg);
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: var(--shadow-xl);
}

/* Close button */
.consent-close {
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.consent-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Illustration */
.consent-illustration {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-lg);
}

/* Content */
.consent-content {
  margin-bottom: var(--spacing-lg);
}

.consent-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-sm);
}

.consent-description {
  font-size: var(--font-size-base);
  line-height: 1.6;
  color: var(--text-secondary);
  margin: 0;
}

/* Actions */
.consent-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
}

.consent-accept-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-xl);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.consent-accept-btn:hover:not(:disabled) {
  background: var(--color-primary-hover);
  box-shadow: 0 4px 12px rgba(30, 41, 59, 0.3);
}

.consent-accept-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.consent-accept-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.consent-decline-link {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  padding: var(--spacing-xs) var(--spacing-sm);
  transition: color var(--transition-fast);
}

.consent-decline-link:hover:not(:disabled) {
  color: var(--text-secondary);
  text-decoration: underline;
}

.consent-decline-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Footer note */
.consent-footnote {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin: 0;
  line-height: 1.5;
}

/* Transitions */
.consent-modal-enter-active,
.consent-modal-leave-active {
  transition: opacity 0.25s ease;
}

.consent-modal-enter-active .consent-modal,
.consent-modal-leave-active .consent-modal {
  transition: transform 0.25s ease;
}

.consent-modal-enter-from,
.consent-modal-leave-to {
  opacity: 0;
}

.consent-modal-enter-from .consent-modal,
.consent-modal-leave-to .consent-modal {
  transform: scale(0.95);
}

/* Mobile: full-screen */
@media (max-width: 767px) {
  .consent-overlay {
    padding: 0;
    align-items: stretch;
  }

  .consent-modal {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: var(--spacing-xl);
  }

  .consent-close {
    position: fixed;
    top: var(--spacing-md);
    left: var(--spacing-md);
    z-index: 1;
    background: var(--bg-secondary);
    box-shadow: var(--shadow-sm);
  }

  .consent-illustration svg {
    width: 140px;
    height: 140px;
  }
}
</style>
